import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { type FormEvent, useEffect, useMemo } from "react";
import { z } from "zod";
import { components as c } from "@/components";
import { searchForTemplateAiWizardNavigation } from "@/features/ai-builder/useTemplateAiDraftStore";
import { TemplateStepFooter } from "@/features/templates/components/TemplateStepFooter";
import { useFinalizeTemplateWizard } from "@/features/templates/hooks/useFinalizeTemplateWizard";
import { useTemplateNavigationStore } from "@/features/templates/hooks/useTemplateNavigationStore";
import { useAuth } from "@/utils/auth";
import { templateSettingsSchema, TrackingType, type TemplateSettingsSchemaT } from "@patient-watch/types";
import {
    Questionnaire,
    QuestionnaireChoice,
    QuestionnaireChoiceDescription,
    QuestionnaireChoices,
    QuestionnaireItem,
} from "@patient-watch/ui/questionnaire";

/** Two choices only. `null` (no choice yet) keeps the organisation default on the diary. */
type HospitalNumberModeT = "collect" | "skip";

function stopSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    event.stopPropagation();
}

function modeFromSetting(value: boolean | null | undefined): HospitalNumberModeT | null {
    if (value === true) return "collect";
    if (value === false) return "skip";
    return null;
}

function settingFromMode(mode: HospitalNumberModeT): boolean {
    return mode === "collect";
}

const hospitalOptions: { value: HospitalNumberModeT; label: string; hint: string }[] = [
    { value: "collect", label: "Collect", hint: "Patients are asked for a hospital number during setup." },
    { value: "skip", label: "Don't collect", hint: "Patients are not asked for a hospital number." },
];

/**
 * Admin-only step, before the trackers: whether patients give a hospital number for diaries from this template.
 */
const TemplateHospitalNumberStep = () => {
    const auth = useAuth();
    const navigate = useNavigate();
    const { templateId } = Route.useParams();
    const search = Route.useSearch();
    const { currentStep, setCurrentStep, markStepComplete, nextStep, updateEventSteps, getNextStepRoute } =
        useTemplateNavigationStore();
    const { finalize, isSubmitting, isTemplateOwnerLookupBlockingSubmit } = useFinalizeTemplateWizard(
        templateId,
        search as Partial<TemplateSettingsSchemaT>
    );

    const mode = modeFromSetting(search.require_hospital_number);
    const effectiveEvents = useMemo(
        () => (search.required_events ?? []).filter((event) => event !== "skip") as TrackingType[],
        [search.required_events]
    );

    // A reload lands here with only the base steps in the store, so rebuild the steps that follow.
    useEffect(() => {
        updateEventSteps(effectiveEvents);
        setCurrentStep("hospital-number");
    }, [effectiveEvents, updateEventSteps, setCurrentStep]);

    const choose = (next: HospitalNumberModeT) => {
        const nextSettings: Partial<TemplateSettingsSchemaT> = {
            ...(search as Partial<TemplateSettingsSchemaT>),
            require_hospital_number: settingFromMode(next),
        };
        const payload = searchForTemplateAiWizardNavigation(templateId, auth?.id ?? "", nextSettings);
        if (!payload) return;
        navigate({
            to: "/templates/$templateId/hospital-number",
            params: { templateId },
            search: payload as never,
            replace: true,
        });
    };

    const goNext = () => {
        const route = getNextStepRoute(currentStep, { templateId });
        if (!route) {
            finalize(search as Partial<TemplateSettingsSchemaT>);
            return;
        }
        const payload = searchForTemplateAiWizardNavigation(templateId, auth?.id ?? "", search as never);
        if (!payload) return;
        markStepComplete("hospital-number");
        nextStep();
        navigate({ ...route, search: payload as never });
    };

    return (
        <div className="space-y-8">
            <c.ui.Card>
                <c.ui.CardHeader>
                    <c.ui.CardTitle>Hospital number</c.ui.CardTitle>
                </c.ui.CardHeader>
                <c.ui.CardContent>
                    <Questionnaire onSubmit={stopSubmit} onReset={stopSubmit}>
                        <QuestionnaireItem name="require_hospital_number" aria-label="Hospital number">
                            <QuestionnaireChoices>
                                {hospitalOptions.map((option) => (
                                    <QuestionnaireChoice
                                        key={option.value}
                                        value={option.value}
                                        checked={mode === option.value}
                                        onChange={() => choose(option.value)}
                                        className="items-start"
                                        inputProps={
                                            {
                                                "data-testid": `hospital-number-${option.value}`,
                                            } as never
                                        }
                                    >
                                        <span className="flex flex-col gap-0.5">
                                            <span className="font-medium">{option.label}</span>
                                            <QuestionnaireChoiceDescription>
                                                {option.hint}
                                            </QuestionnaireChoiceDescription>
                                        </span>
                                    </QuestionnaireChoice>
                                ))}
                            </QuestionnaireChoices>
                        </QuestionnaireItem>
                    </Questionnaire>
                </c.ui.CardContent>
            </c.ui.Card>

            <TemplateStepFooter
                onPrimaryClick={goNext}
                isLoadingPrimary={isSubmitting}
                disabledPrimary={isTemplateOwnerLookupBlockingSubmit}
            />
        </div>
    );
};

const hospitalSearchSchema = templateSettingsSchema.partial().extend({
    required_events: z.array(z.union([TrackingType, z.literal("skip")])).optional(),
});

export const Route = createFileRoute("/_app/templates/$templateId/hospital-number")({
    component: TemplateHospitalNumberStep,
    validateSearch: (search) => hospitalSearchSchema.parse(search),
});
